// src/components/MicroCreditModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientService from '../services/clientService';

const PAGE_SIZE = 20;

const normalize = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

function addDays(dateStr, days) {
  if (!dateStr || !days) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + Number(days));
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function MicroCreditModal({
  show,
  onHide,
  onSave,
  initialData = null,
}) {
  const init = initialData ?? {};

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      clientId: init.clientId ?? null,
      currency: init.currency ?? 'HTG',
      principalAmount: init.principalAmount ?? '',
      feeAmount: init.feeAmount ?? '',
      startDate: init.startDate ? String(init.startDate).slice(0, 10) : '',
      durationDays: init.durationDays ?? '',
      note: init.note ?? '',
    },
  });

  const [allClients, setAllClients] = useState([]);

  const principalAmount = parseFloat(watch('principalAmount')) || 0;
  const feeAmount = Math.max(0, parseFloat(watch('feeAmount')) || 0);
  const durationDays = parseInt(watch('durationDays'), 10) || 0;
  const startDate = watch('startDate') || '';
  const totalRepayable = principalAmount + feeAmount;
  const endDate = addDays(startDate, durationDays);

  useEffect(() => {
    if (!show) return;

    reset({
      clientId: init.clientId ?? null,
      currency: init.currency ?? 'HTG',
      principalAmount: init.principalAmount ?? '',
      feeAmount: init.feeAmount ?? '',
      startDate: init.startDate ? String(init.startDate).slice(0, 10) : '',
      durationDays: init.durationDays ?? '',
      note: init.note ?? '',
    });

    clientService.list()
      .then(setAllClients)
      .catch(() => setAllClients([]));
  }, [
    show,
    init.clientId,
    init.currency,
    init.principalAmount,
    init.feeAmount,
    init.startDate,
    init.durationDays,
    init.note,
    reset,
  ]);

  const toOption = (c) => ({
    value: c.id,
    label: `${c.firstName || ''} ${c.lastName || ''} — ${c.nifCin || 'NIF/CIN ?'}`.trim(),
  });

  const selectedClientOption = useMemo(() => {
    const currentId = watch('clientId');
    if (!currentId) return null;
    const found = allClients.find(c => c.id === currentId);
    return found ? toOption(found) : { value: currentId, label: `Client #${currentId}` };
  }, [allClients, watch('clientId')]);

  const loadClientOptions = async (inputValue, loadedOptions, { page }) => {
    const q = normalize(inputValue);
    const filtered = allClients.filter(c => {
      const hay = normalize(
        `${c.firstName || ''} ${c.lastName || ''} ${c.nifCin || ''} ${c.email || ''} ${c.phone || ''}`
      );
      return hay.includes(q);
    });

    const start = (page - 1) * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE);
    const options = slice.map(toOption);
    const hasMore = start + PAGE_SIZE < filtered.length;

    return {
      options,
      hasMore,
      additional: { page: page + 1 },
    };
  };

  const submit = (vals) => {
    onSave({
      clientId: vals.clientId,
      currency: vals.currency,
      principalAmount: Number(principalAmount.toFixed(2)),
      feeAmount: Number(feeAmount.toFixed(2)),
      startDate: vals.startDate,
      durationDays: Number(vals.durationDays),
      note: String(vals.note || '').trim() || null,
      ...(init.id ? { id: init.id } : {}),
    });
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {init.id ? 'Modifier un Micro Crédit' : 'Ajouter un Micro Crédit'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Client *</Form.Label>
            <Controller
              control={control}
              name="clientId"
              rules={{ required: 'Veuillez choisir un client' }}
              render={({ field }) => (
                <AsyncPaginate
                  value={selectedClientOption}
                  onChange={(opt) => field.onChange(opt?.value ?? null)}
                  loadOptions={loadClientOptions}
                  additional={{ page: 1 }}
                  debounceTimeout={300}
                  placeholder="Rechercher par nom, NIF/CIN, téléphone…"
                  isClearable
                />
              )}
            />
            {errors.clientId && (
              <Form.Text className="text-danger">{errors.clientId.message}</Form.Text>
            )}
          </Form.Group>

          <Row className="g-3">
            <Col md={4}>
              <Form.Group controlId="currency">
                <Form.Label>Devise *</Form.Label>
                <Form.Select {...register('currency', { required: 'Requis' })}>
                  <option value="HTG">HTG</option>
                  <option value="USD">USD</option>
                </Form.Select>
                {errors.currency && <Form.Text className="text-danger">{errors.currency.message}</Form.Text>}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="principalAmount">
                <Form.Label>Montant à prêter *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('principalAmount', {
                    required: 'Requis',
                    min: { value: 0.01, message: '> 0' },
                  })}
                />
                {errors.principalAmount && <Form.Text className="text-danger">{errors.principalAmount.message}</Form.Text>}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="feeAmount">
                <Form.Label>Frais *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  {...register('feeAmount', {
                    required: 'Requis',
                    min: { value: 0, message: '>= 0' },
                  })}
                />
                {errors.feeAmount && <Form.Text className="text-danger">{errors.feeAmount.message}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group controlId="startDate">
                <Form.Label>Date de création *</Form.Label>
                <Form.Control
                  type="date"
                  {...register('startDate', { required: 'Requis' })}
                />
                {errors.startDate && <Form.Text className="text-danger">{errors.startDate.message}</Form.Text>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="durationDays">
                <Form.Label>Durée totale (jours) *</Form.Label>
                <Form.Control
                  type="number"
                  {...register('durationDays', {
                    required: 'Requis',
                    min: { value: 1, message: '>= 1' },
                  })}
                />
                {errors.durationDays && <Form.Text className="text-danger">{errors.durationDays.message}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mt-3" controlId="note">
            <Form.Label>Note</Form.Label>
            <Form.Control as="textarea" rows={3} {...register('note')} />
          </Form.Group>

          <Row className="mt-3">
            <Col><Form.Text>Total à encaisser : {totalRepayable.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Date de fin : {endDate || '—'}</Form.Text></Col>
          </Row>

          {init.id && (
            <>
              <Form.Group className="mt-3" controlId="accountNumber">
                <Form.Label>Numéro de compte</Form.Label>
                <Form.Control readOnly value={init.accountNumber || ''} />
              </Form.Group>

              <Form.Group className="mt-3" controlId="statusReadOnly">
                <Form.Label>Statut actuel</Form.Label>
                <Form.Control readOnly value={init.status || ''} />
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" variant="primary">
            {init.id ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}